import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { Button, ErrorPanel, Panel, Skeleton, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  useGetVehicleTypeQuery,
  useUpdateVehicleTypeMutation,
} from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { addToast } from "@/utils";
import { CategoryPicker } from "../AddVehicleType/CategoryPicker";
import { DiscardModal } from "../AddVehicleType/DiscardModal";
import { StepOneFields } from "../AddVehicleType/StepOneFields";
import { vehicleTypeFormSchema, VehicleTypeFormData } from "../AddVehicleType/form";

export const EditVehicleTypePanel = () => {
  const { vehicleTypeId = "" } = useParams<{ vehicleTypeId: string }>();
  const navigate = useNavigate();
  const { data: vehicleType, isFetching, error } = useGetVehicleTypeQuery(vehicleTypeId);
  const [step, setStep] = useState<1 | 2>(1);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [updateVehicleType, { isLoading }] = useUpdateVehicleTypeMutation();

  const form = useForm<VehicleTypeFormData>({
    resolver: zodResolver(vehicleTypeFormSchema),
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!vehicleType) return;
    form.reset({
      name: vehicleType.name,
      capacity: vehicleType.vehicleCapacity.pax,
      bags: vehicleType.vehicleCapacity.bags,
      waitTimeCost: vehicleType.waitTimeCost / 100,
      costAdjustment: vehicleType.perDistanceUnitMultiplier,
      customCommissionEnabled: vehicleType.customCommission.enabled,
      customCommissionRate: vehicleType.customCommission.rate,
      vehicleCategoryUuid: vehicleType.vehicleCategory?.uuid ?? "",
    });
  }, [vehicleType, form]);

  useEffect(() => {
    if (vehicleType?.isDefault) {
      addToast("danger", "System defaults can't be edited.");
      navigate("./..");
    }
  }, [vehicleType, navigate]);

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;
  if (isFetching || !vehicleType) return <Skeleton />;
  if (vehicleType.isDefault) return null;

  const handleNext = async () => {
    const valid = await form.trigger([
      "name",
      "capacity",
      "bags",
      "waitTimeCost",
      "costAdjustment",
      "customCommissionEnabled",
      "customCommissionRate",
    ]);
    if (valid) setStep(2);
  };

  const handleCancel = () => setDiscardOpen(true);

  const handleSave = form.handleSubmit(async (data) => {
    try {
      await updateVehicleType({
        id: vehicleType.uuid,
        name: data.name,
        is_active: vehicleType.isActive,
        per_distance_unit_multiplier: data.costAdjustment,
        wait_time_cost: Math.round(data.waitTimeCost * 100),
        vehicle_capacity: { pax: data.capacity, bags: data.bags },
        vehicle_category_uuid: data.vehicleCategoryUuid,
        custom_commission: data.customCommissionEnabled
          ? { enabled: true, rate: data.customCommissionRate ?? 0 }
          : { enabled: false, rate: 0 },
      }).unwrap();
      addToast("success", "Vehicle Type updated successfully");
      navigate("./..");
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((m) => addToast("danger", m));
    }
  });

  const isStep1 = step === 1;

  return (
    <FormProvider {...form}>
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col items-center px-5 py-4">
        <Panel className={isStep1 ? "w-full max-w-[550px]" : "w-full max-w-[800px]"}>
          <div className="flex flex-col gap-2">
            <Typography className="text-lg font-semibold leading-5 text-neutral-black">
              {isStep1 ? "Step 1: Edit Vehicle Type" : "Step 2: Edit Vehicle Type"}
            </Typography>
            <Typography className="text-base leading-5 text-neutral-dark-gray">
              {isStep1 ? (
                "Update the details for this operator vehicle type."
              ) : (
                <>
                  Map your vehicle type: Select the most suitable platform vehicle category for{" "}
                  <span className="text-info">{form.watch("name")?.trim() || vehicleType.name}</span>. This helps RideMinder match your
                  operator vehicle type correctly when you offload jobs.
                </>
              )}
            </Typography>
          </div>
          <div className="mt-6">
            {isStep1 ? <StepOneFields /> : <CategoryPicker vehicleTypeName={form.watch("name") || vehicleType.name} />}
          </div>
          <div className="mt-8 flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            {isStep1 ? (
              <Button variant="primary" onClick={handleNext} endIcon="ArrowRight">
                Next
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setStep(1)} startIcon="ArrowLeft" disabled={isLoading}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isLoading || !form.watch("vehicleCategoryUuid")}
                >
                  {isLoading ? <Spinner /> : "Save Changes"}
                </Button>
              </>
            )}
          </div>
        </Panel>
      </div>
      <DiscardModal open={discardOpen} onClose={() => setDiscardOpen(false)} onConfirm={() => navigate("./..")} mode="edit" />
    </FormProvider>
  );
};
